import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import type { VerifyResponse } from './schema';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface VerifyError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): VerifyError {
  const e = new Error(stage) as VerifyError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

/**
 * `POST /auth/device/verify` — approves or denies a pending device-code
 * grant for the authenticated user. When the client is third-party and the
 * user has not yet granted consent, the response is `{consent_required:
 * true, …}` rather than completing the flow.
 */
export function useDeviceVerify() {
  const api = useApi();
  return useMutation<VerifyResponse, VerifyError, { user_code: string; approve: boolean }>({
    mutationFn: async (input) => {
      const { data, error, response } = await api.POST('/auth/device/verify', { body: input });
      if (error || !data) throw toError('device_verify_failed', response, error);
      return data;
    },
  });
}
